import { useState, useEffect } from 'react';
import { useExperience } from '../../context/ExperienceContext';

export type DarumaPose = 'wave' | 'point' | 'think' | 'explain' | 'celebrate' | 'travel';

interface DarumaBubbleProps {
  pose: DarumaPose;
  message: string;
  size?: 'sm' | 'md' | 'lg';
  position?: 'left' | 'right';
  animate?: boolean;
  className?: string;
}

const SIZE_PX: Record<'sm' | 'md' | 'lg', number> = { sm: 48, md: 80, lg: 120 };

function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

export function DarumaBubble({
  pose,
  message,
  size = 'md',
  position = 'left',
  animate = false,
  className = '',
}: DarumaBubbleProps) {
  const { isManga, isUkiyo, isMangaV3 } = useExperience();
  const mobile = useIsMobile();
  const effectiveSize = mobile ? 'sm' : size;
  const px = SIZE_PX[effectiveSize];
  const suffix = (isManga || isMangaV3) ? '-manga' : isUkiyo ? '-ukiyo' : '';
  const src = `/images/mascot/daruma-${pose}${suffix}.png`;
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => { setImgFailed(false); }, [src]);

  const bubbleStyle: React.CSSProperties = (isManga || isMangaV3)
    ? { border: '2px solid #0a0a0a', borderRadius: 0 }
    : isUkiyo
      ? { border: '1px solid rgba(184,134,11,0.5)', borderRadius: 4 }
      : { borderRadius: 12 };

  const daruma = imgFailed ? (
    <div
      style={{
        width: px,
        height: px,
        borderRadius: '50%',
        background: '#dc2626',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: px * 0.5,
        flexShrink: 0,
      }}
      aria-hidden
    >
      🎯
    </div>
  ) : (
    <img
      src={src}
      alt="Daruma-kun"
      width={px}
      height={px}
      style={{ flexShrink: 0, objectFit: 'contain' }}
      onError={() => setImgFailed(true)}
      className={animate ? 'daruma-float' : undefined}
    />
  );

  return (
    <div
      className={`flex items-center gap-3 ${position === 'right' ? 'flex-row-reverse' : ''} ${className}`}
    >
      {daruma}
      <div
        className="relative flex-1 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-800 dark:text-gray-100"
        style={bubbleStyle}
      >
        {message}
      </div>
      <style>{`
        @keyframes darumaFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        .daruma-float { animation: darumaFloat 2s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
